import { DateTime, Interval } from 'luxon'

export const VIEW_LIST = 'list'
export const VIEW_MONTH = 'month'
export const VIEW_WEEK = 'week'
export const VIEW_DAY = 'day'

/** US-style week: Sunday (column order matches My Schedule + Academy). */
export function startOfUsWeek(d) {
  const c = d.startOf('day')
  return c.minus({ days: c.weekday % 7 || 0 })
}

export function endOfUsWeek(d) {
  return startOfUsWeek(d)
    .plus({ days: 6 })
    .endOf('day')
}

/**
 * Move the calendar anchor by whole months (toolbar prev/next in month view).
 * @param {import('luxon').DateTime} anchorDate
 * @param {number} deltaMonths negative = previous month, positive = next month
 */
export function shiftCalendarMonth(anchorDate, deltaMonths) {
  return anchorDate.plus({ months: deltaMonths }).startOf('month')
}

/**
 * Move the schedule anchor by whole calendar "steps" for month / week / day
 * (same notion as toolbar prev/next). Used to prefetch adjacent ranges.
 * @param {string} viewMode VIEW_MONTH | VIEW_WEEK | VIEW_DAY
 * @param {import('luxon').DateTime} anchorDate
 * @param {number} stepOffset e.g. -2 = two months/weeks/days back, +1 = next period
 * @returns {import('luxon').DateTime}
 */
export function getCalendarNavAnchor(viewMode, anchorDate, stepOffset) {
  if (viewMode === VIEW_MONTH) {
    return shiftCalendarMonth(anchorDate, stepOffset)
  }
  if (viewMode === VIEW_WEEK) {
    return anchorDate.plus({ days: 7 * stepOffset })
  }
  if (viewMode === VIEW_DAY) {
    return anchorDate.plus({ days: stepOffset })
  }
  return anchorDate
}

/**
 * @param {{ from: string, to: string | null } | null} startDateTime
 * @returns {boolean} true if the query should not run
 */
export function isScheduleDateRangeQueryInvalid(startDateTime) {
  if (startDateTime == null) return true
  if (!startDateTime.from) return true
  if (!startDateTime.to) return false
  return !Interval.fromDateTimes(
    DateTime.fromISO(startDateTime.from),
    DateTime.fromISO(startDateTime.to),
  ).isValid
}

export function getScheduleOrderBy() {
  return [
    {
      field: 'START_DATE_TIME',
      direction: 'ASC',
    },
  ]
}

/**
 * @param {string} viewMode
 * @param {import('luxon').DateTime} anchorDate
 */
export function getScheduleToolbarLabel(viewMode, anchorDate) {
  if (viewMode === VIEW_LIST) {
    // The page already has a "Schedule" header, so the toolbar label is
    // suppressed in list view to avoid duplicating it.
    return ''
  }
  if (viewMode === VIEW_MONTH) {
    return anchorDate.toFormat('LLLL yyyy')
  }
  if (viewMode === VIEW_WEEK) {
    const a = startOfUsWeek(anchorDate)
    const b = endOfUsWeek(anchorDate)
    if (a.hasSame(b, 'day')) {
      return a.toFormat('LLL d, yyyy')
    }
    if (a.month === b.month && a.year === b.year) {
      return `${a.toFormat('LLL d')} – ${b.toFormat('d, yyyy')}`
    }
    if (a.year === b.year) {
      return `${a.toFormat('LLL d')} – ${b.toFormat('LLL d, yyyy')}`
    }
    return `${a.toFormat('LLL d, yyyy')} – ${b.toFormat('LLL d, yyyy')}`
  }
  if (viewMode === VIEW_DAY) {
    return anchorDate.toFormat('cccc, LLL d, yyyy')
  }
  return ''
}

/**
 * When the range has no sessions / events (same query as the grid).
 * @param {string} view VIEW_MONTH | VIEW_WEEK | VIEW_DAY
 */
export function getEmptyCalendarViewMessage(view) {
  if (view === VIEW_MONTH) return 'No bookings this month.'
  if (view === VIEW_WEEK) return 'No bookings this week.'
  if (view === VIEW_DAY) return 'No bookings this day.'
  return 'No bookings to show.'
}
